<template>
  <div class="the-schedule-container">
    <div class="pre-round" v-if="hasPreRoundShecdule">
      <p @click="showPreRoundSegments = !showPreRoundSegments">
        <template v-if="!showPreRoundSegments">Show</template
        ><template v-else>Hide</template> pre round schedule
      </p>
      <p></p>
    </div>
    <slide-up-down :active="showPreRoundSegments" :duration="500">
      <div
        v-if="event.registrationOpenAt"
        class="registration-open segment"
        :class="{ completed: moment(event.registrationOpenAt).isBefore() }"
      >
        <div class="left">
          <RegistrationOpen
            :color="
              moment(event.registrationOpenAt).isBefore()
                ? 'blizzard'
                : 'sunrise'
            "
          />
        </div>
        <div class="right">
          <p class="segment-title"><b>Registration opens</b></p>
          <p class="segment-subtitle">
            {{
              dateFormatter(event.registrationOpenAt, 'ddd D MMM [at] HH:mm')
            }}
          </p>
          <p class="stages-copy" v-if="event.registrationStages.length > 0">
            Stages enabled
          </p>
        </div>
      </div>
      <div
        class="registration-close segment"
        v-if="event.registrationCloseAt"
        :class="{ completed: moment(event.registrationCloseAt).isBefore() }"
      >
        <div class="left">
          <Off
            :color="
              moment(event.registrationCloseAt).isBefore() ? 'blizzard' : null
            "
          />
        </div>
        <div class="right">
          <p class="segment-title"><b>Registration closes</b></p>
          <p class="segment-subtitle">
            {{
              dateFormatter(event.registrationCloseAt, 'ddd D MMM [at] HH:mm')
            }}
          </p>
        </div>
      </div>
      <div
        class="checkin-start segment"
        v-if="event.checkInStartsAt"
        :class="{ completed: moment(event.checkInStartsAt).isBefore() }"
      >
        <div class="left">
          <CheckInOpens
            :color="
              moment(event.checkInStartsAt).isBefore() ? 'blizzard' : null
            "
          />
        </div>
        <div class="right">
          <p class="segment-title"><b>Check-in opens</b></p>
          <p class="segment-subtitle">
            {{ dateFormatter(event.checkInStartsAt, 'ddd D MMM [at] HH:mm') }}
          </p>
        </div>
      </div>
      <div
        class="checkin-end segment"
        v-if="event.checkInEndsAt"
        :class="{ completed: moment(event.checkInEndsAt).isBefore() }"
      >
        <div class="left">
          <CheckInCloses
            :color="
              moment(event.checkInEndsAt).isBefore() ? 'blizzard' : 'dusk'
            "
          />
        </div>
        <div class="right">
          <p class="segment-title"><b>Check-in closes</b></p>
          <p class="segment-subtitle">
            {{ dateFormatter(event.checkInEndsAt, 'ddd D MMM [at] HH:mm') }}
          </p>
        </div>
      </div>
    </slide-up-down>
    <template v-for="(round, roundIndex) in event.games[0].rounds">
      <div
        class="pool segment"
        :class="{
          completed: round.status === 'COMPLETED',
          last: roundIndex + 1 === event.games[0].rounds.length,
        }"
      >
        <div class="left">
          <div
            class="round-icon-container"
            :class="{ live: round.status === 'OPEN' }"
          >
            <RoundIcon :color="poolIconColor(round.status)" />
          </div>
        </div>
        <div class="right">
          <p class="segment-title">
            <b>Round {{ roundIndex + 1 }}</b>
          </p>
          <p class="segment-subtitle">
            {{ dateFormatter(round.date, 'ddd D MMM [at] HH:mm') }}
          </p>
          <div class="finished" v-if="round.status === 'COMPLETED'">
            <b>Finished</b>
            <router-link
              :to="{
                name: 'public-event-game-round-division-results',
                params: {
                  eventId: event.id,
                  gameId: event.games[0].id,
                  roundId: round.id,
                  divisionId: event.divisions[0].id,
                },
              }"
              >View results</router-link
            >
          </div>
          <div class="ongoing" v-if="round.status === 'OPEN'">
            <b>Live</b>
            <router-link
              :to="{
                name: 'public-event-game-round-division-results',
                params: {
                  eventId: event.id,
                  gameId: event.games[0].id,
                  roundId: round.id,
                  divisionId: event.divisions[0].id,
                },
              }"
              >Follow live</router-link
            >
          </div>
          <div class="upcoming" v-if="round.status === 'CLOSED'">
            <b>Upcoming</b>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import RoundIcon from '@/assets/icons/RoundIcon'
import CheckInOpens from '@/assets/icons/CheckInOpens'
import CheckInCloses from '@/assets/icons/CheckInCloses'
import RegistrationOpen from '@/assets/icons/RegistrationOpen'
import Off from '@/assets/icons/Off'
import SlideUpDown from 'vue-slide-up-down'

import dateFormats from '@/mixins/dateFormats.js'
export default {
  name: 'TheEventSchedule',
  components: {
    RoundIcon,
    CheckInOpens,
    CheckInCloses,
    RegistrationOpen,
    Off,
    SlideUpDown,
  },
  props: ['event'],
  mixins: [dateFormats],
  data() {
    return {
      showPreRoundSegments: false,
      roundIdToShow: null,
    }
  },
  computed: {
    hasPreRoundShecdule() {
      return (
        this.event.registrationOpenAt ||
        this.event.registrationCloseAt ||
        this.event.checkInStartsAt ||
        this.event.checkInEndsAt
      )
    },
  },
  methods: {
    formattedPlayFormat(playFormat) {
      switch (playFormat) {
        case 'BEST_SCORE':
          return 'Best score'
        case 'BEST_SHOT':
          return 'Best shot'
        case 'MODIFIED_BEST_SHOT':
          return 'Modified best shot'
        case 'GREENSOME':
          return 'Greensome'
        case 'MODIFIED_GREENSOME':
          return 'Modified greensome'
        case 'COMBINED_SCORE':
          return 'Combined score'
        case 'TOUGH_SHOT':
          return 'Tough shot'
        case 'WORST_SHOT':
          return 'Worst shot'
        case 'ALTERNATE_THROW':
          return 'Alternate throw'

        default:
          break
      }
    },
    openGroupsPopup(round) {
      this.roundIdToShow = round.id
      this.showGroupsPopup = true
    },
    poolIconColor(status) {
      switch (status) {
        case 'OPEN':
          return 'grass'
        case 'COMPLETED':
          return 'blizzard'
        default:
          return 'midnight'
      }
    },
    divisionsInPool(pool, divisions, poolsInIdivisions) {
      return divisions.filter((division) =>
        poolsInIdivisions.some(
          (poolInDivision) =>
            poolInDivision.poolId === pool.id &&
            poolInDivision.divisionId === division.id
        )
      )
    },
  },
  mounted() {
    const anyPoolStarted = this.event.rounds.some((round) =>
      round.pools.some((pool) =>
        ['OPEN', 'PAUSED', 'COMPLETED'].includes(pool.status)
      )
    )

    if (!anyPoolStarted) {
      this.showPreRoundSegments = true
    }
  },
  beforeCreate() {},
  destroyed() {},
}
</script>

<style lang="scss" scoped>
@import '@/styles/Typography';

.play-format-label {
  font-size: 12px;
  margin-left: 12px;
  line-height: 12px;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid $midnight;
}

.live {
  position: relative;
  z-index: 0;

  &::before {
    z-index: -1;
    content: '';
    position: absolute;
    top: 3px;
    left: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: $grass;
    animation: pulse 1.5s ease-in-out infinite;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  20% {
    opacity: 1;
  }

  100% {
    transform: scale(2.1);
    opacity: 0;
  }
}

.the-schedule-container {
  margin-top: 12px;
}

.pre-round {
  margin-bottom: 20px;

  p {
    cursor: pointer;
    font-size: 14px;
    color: $ocean;
  }
}

.segment {
  display: flex;
  position: relative;
  align-items: flex-start;
  padding: 8px 0 24px 0;

  &::before {
    z-index: 0;
    content: '';
    position: absolute;
    left: 11px;
    top: 12px;
    bottom: 0;
    width: 1px;
    background-color: $blizzard;
  }

  &.completed {
    p {
      color: $blizzard;
    }
  }

  &.last {
    padding-bottom: 0;

    &::before {
      display: none;
    }
  }
}

.right {
  padding-left: 12px;
}

.left {
  z-index: 0;
  width: 24px;
  background-color: white;
  padding-bottom: 6px;
}

.segment-title {
  padding-top: 4px;
  display: flex;
  align-items: center;
  font-size: 18px;
  line-height: 18px;
}

.segment-subtitle {
  font-size: 16px;
  line-height: 24px;
  margin-top: 4px;
}

.stages-copy {
  font-size: 14px;
  line-height: 21px;
  color: $strom;
}

.course-info {
  margin-top: 4px;
  font-size: 14px;
  line-height: 21px;
}

.divisions-info {
  margin-top: 6px;
  font-size: 14px;
  line-height: 21px;
}

.ongoing,
.finished,
.upcoming {
  margin-top: 8px;
  font-size: 16px;
  line-height: 21px;

  b {
    font-size: 12px;
    padding: 4px 6px;
    line-height: 12px;
    border-radius: 4px;
  }

  a {
    margin-left: 12px;
  }

  .view-groups {
    display: inline;
    color: $ocean;
    cursor: pointer;
    margin-left: 12px;
  }
}

.upcoming {
  b {
    background-color: #ffe2da;
    color: $sunset;
  }
}

.ongoing {
  b {
    background-color: #ccf5eb;
    color: $grass;
  }
}

.finished {
  b {
    background-color: $fog;
    color: $strom;
  }
}

@media (min-width: 480px) and (orientation: landscape) {
}

@media (min-width: 544px) {
}

@media (min-width: 768px) {
}

@media (min-width: 1200px) {
}
</style>
